import React from 'react';
import {
  generatePath,
  useHistory,
} from 'react-router-dom';
import update from 'immutability-helper';
import { t } from 'ttag';
import {
  makeVar,
  useReactiveVar,
} from '@apollo/client';

import {
  notifyError,
  notifySuccess,
} from '@utils/notifications';
import { getFieldsSingleError } from '@utils/common';
import { useTabletOrWider } from '@utils/mediaQuery';
import {
  basketHash,
  basketOnMain,
  mainPage,
} from '@legacyUrls';
import {
  checkoutBuyer,
} from '@urls';
import {
  ecommerceTrigger,
  sendDynamicRemarketingEvent,
  sendRTBHouseAddToCartEvent,
} from '@utils/analytics';
import { useAppContext } from '@contexts/AppContext';

import {
  useBasketAddProductMutation,
  useBasketDeleteProductMutation,
  useBasketQuery,
  useBasketUpdateProductCountMutation,
} from './graphql.v4.generated';

type AddProductArg = {
  productId: number
  sizeId: undefined | number | number[]
};
type IsInCartArg = {
  productId: number
  sizeId: undefined | number | number[]
};
type UpdateCountArg = {
  basketProductId: number
  count: number
};
type UseBasketArg = {
  lazy?: boolean
};

// Required to share modal open state between different bundles
// for example on product page, where modal can be opened through "Buy" button
// and through cart icon on NavBar (button and navbar are in different bundles)
let isModalOpenVar = makeVar(false);
if (typeof window !== 'undefined') {
  if (!window.IS_CART_MODAL_OPEN_VAR) {
    window.IS_CART_MODAL_OPEN_VAR = makeVar(
      location.hash === basketHash
      || location.pathname.includes(basketOnMain),
    );
  }

  isModalOpenVar = window.IS_CART_MODAL_OPEN_VAR;
}

export const useBasket = (arg: UseBasketArg = {}) => {
  const { isOutlet } = useAppContext();

  const { lazy } = arg;

  // UI
  const history = useHistory();
  const isTabletOrWider = useTabletOrWider();
  const isOpen = useReactiveVar(isModalOpenVar);
  const open = () => {
    if (isTabletOrWider) {
      history.replace({ ...history.location, hash: basketHash });
    } else {
      history.push({ ...history.location, hash: basketHash });
    }
    isModalOpenVar(true);
  };
  const close = () => {
    // Replace pathname on mainPage when we open basket through basketOnMain route
    const location = history.location.pathname === basketOnMain
      ? { ...history.location, pathname: mainPage }
      : history.location;
    if (isTabletOrWider) {
      history.replace({ ...location, hash: '' });
    } else {
      history.push({ ...location, hash: '' });
    }
    isModalOpenVar(false);
  };

  // GraphQL queries/mutations
  const { data, loading } = useBasketQuery({
    context: {
      v4: true,
      batch: true,
      ignoreAuthError: true,
    },
    skip: lazy ? !isOpen : false,
    ssr: false,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
  });
  const [addProduct] = useBasketAddProductMutation({
    context: {
      v4: true,
    },
  });
  const [deleteProductMutate] = useBasketDeleteProductMutation({
    context: {
      v4: true,
    },
    onCompleted: ({ deleteBasketProduct }) => {
      if (deleteBasketProduct.errors?.length) {
        // TODO(a.kravchuk): remove ignore after gateway types fix
        // @ts-ignore
        const errors = getFieldsSingleError(deleteBasketProduct.errors);
        notifyError(
          <div>
            {Object.values(errors).map((e, i) => (
              <React.Fragment key={i}>
                {e}
                <br />
              </React.Fragment>
            ))}
          </div>,
        );
      } else {
        notifySuccess(t`Продукт видалений з кошику`);
      }
    },
  });
  const [updateCountMutate] = useBasketUpdateProductCountMutation({
    context: {
      v4: true,
    },
  });

  const isInBasket = ({ productId, sizeId }: IsInCartArg) => {
    if (lazy) {
      throw new Error("Operation doesn't supported for lazy basket");
    }
    if (!data) {
      return false;
    }

    const isSizeInCart = (singleSizeId: number | undefined) => (
      data.basket.groups.some(
        ({ basketProducts }) => (
          basketProducts.some(
            ({ product, size }) => (
              product!.id === productId && (size?.id === singleSizeId || singleSizeId === undefined)
            ),
          )
        ),
      )
    );

    if (Array.isArray(sizeId)) {
      return sizeId.length ? sizeId.every(isSizeInCart) : isSizeInCart(undefined);
    }

    return isSizeInCart(sizeId);
  };

  const add = async ({ productId, sizeId }: AddProductArg) => {
    if (lazy) {
      throw new Error("Operation doesn't supported for lazy basket");
    }
    if (isInBasket({ productId, sizeId })) {
      open();
      return undefined;
    }

    let sizesIds;
    if (Array.isArray(sizeId)) {
      sizesIds = sizeId;
    } else if (sizeId) {
      sizesIds = [sizeId];
    } else {
      sizesIds = null;
    }

    const { data: responseData } = await addProduct({
      variables: {
        productId,
        sizeIds: sizesIds,
      },
      onCompleted: (res) => {
        res.addBasketProduct.basket.groups.forEach((group) => (
          group.basketProducts.forEach((product) => {
            if (product.product?.id === productId) {
              ecommerceTrigger({
                event: 'add_to_cart',
                products: [product.product],
              });
              sendDynamicRemarketingEvent(
                'add_to_cart',
                [productId],
                String(product.product?.price),
              );
              sendRTBHouseAddToCartEvent(String(productId));
            }
          })
        ));
      },
    });

    notifySuccess(t`Товар успішно додано до кошика`);
    return responseData?.addBasketProduct.basket;
  };

  const deleteProduct: typeof deleteProductMutate = (options) => deleteProductMutate({
    optimisticResponse: !data
      ? undefined
      : () => ({
        deleteBasketProduct: {
          errors: [],
          basket: update(
            data.basket,
            {
              groups: (groups) => groups
                .map(
                  (group) => (
                    {
                      ...group,
                      basketProducts: group.basketProducts.filter(
                        (p) => p.id !== options?.variables?.id,
                      ),
                    }
                  ),
                )
                .filter((group) => !!group.basketProducts.length),
            },
          ),
        },
      }),
    ...options,
  });

  const updateCount = async ({ basketProductId, count }: UpdateCountArg) => {
    await updateCountMutate({
      variables: {
        id: basketProductId,
        newCount: count,
      },
      optimisticResponse: !data
        ? undefined
        : () => ({
          updateBasketProductCount: {
            errors: [],
            basket: update(
              data.basket,
              {
                groups: (groups) => groups.map(
                  (group) => {
                    const basketProductIndex = group.basketProducts.findIndex(
                      (p) => p.id === basketProductId,
                    );

                    // If group contain required product
                    if (basketProductIndex !== -1) {
                      const basketProduct = group.basketProducts[basketProductIndex]!;
                      return update(
                        group,
                        {
                        // 1. Update price for this product (price * count)
                          basketProducts: {
                            [basketProductIndex]: {
                              count: {
                                $set: count,
                              },
                            },
                          },
                          // 2. Update total group price (price - price * oldCount + price * count)
                          info: {
                            $set: {
                              totalPrice: String(
                                +group.info.totalPrice
                              - basketProduct.product!.price! * basketProduct.count
                              + basketProduct.product!.price! * count,
                              ),
                            },
                          },
                        },
                      );
                    }

                    return group;
                  },
                ),
              },
            )!,
          },
        }),
    });
  };

  const buyNow = async ({ productId, sizeId }: AddProductArg) => {
    if (lazy) {
      throw new Error("Operation doesn't supported for lazy basket");
    }
    const isEmpty = !data?.basket.productsCount;
    const basket = await add({ productId, sizeId });
    if (isEmpty) {
      const group = basket?.groups.find(
        (groupFind) => (
          groupFind.basketProducts.find(
            (basketProduct) => (basketProduct.product?.id === productId),
          )
        ),
      );
      if (!group) {
        throw new Error(`Group is not found for product ${productId}`);
      } else if (!group.seller) {
        throw new Error('No seller in group');
      }

      // Go to checkout
      if (isOutlet) {
        history.push(generatePath(checkoutBuyer, { sellerId: group.seller.id }));
      } else {
        const checkoutUrl = history.createHref({
          pathname: generatePath(checkoutBuyer, { sellerId: group.seller.id }),
        });
  
        window.location.href = checkoutUrl;
      }
    } else {
      open();
    }
  };

  return {
    basket: data?.basket,
    loading,
    add,
    deleteProduct,
    isInBasket,
    updateCount,
    buyNow,
    ui: {
      isOpen,
      open,
      close,
    },
  };
};
